import Debouncer from './Debouncer';
import convertToDoc from './convertToDoc';

interface AutoLoadPagesOptions {
	frameId: string; /** The results div */
	scrollableAreaId?: string; /** The element being scrolled */
	nextClass?: string;/** The class name of the next button */
	resultsClass: string; /** The class name of the item to be populated */
	paginationClass: string; /** The class name of the pagination element */
	loadWhenDistanceFromBottom?: number; /** The distance from bottom in pixel values to load in at */
}

export default class AutoLoadPages {
	private options: AutoLoadPagesOptions;

	private resultsEl: HTMLElement;
	private scrollableAreaEl: HTMLElement | Window;
	private paginationEl: HTMLElement;

	private loadNextIfNeededDebouncer: Debouncer;

	private currentlyLoading: boolean;

	constructor(options: AutoLoadPagesOptions) {
		this.options = options;

		this.options.nextClass = options.nextClass || 'next';
		this.options.loadWhenDistanceFromBottom =
			!!options.loadWhenDistanceFromBottom
				? options.loadWhenDistanceFromBottom
				: 200;
		this.scrollableAreaEl =
			!!this.options.scrollableAreaId
				? document.getElementById(this.options.scrollableAreaId)
				: window;

		this.init();
	}

	private destroy(): void {
		if (this.loadNextIfNeededDebouncer) {
			// Setup to check if next page needs to loaded when window scrolled or window resized
			this.scrollableAreaEl.removeEventListener('scroll', this.loadNextIfNeededDebouncer.run);
			window.removeEventListener('load', this.loadNextIfNeededDebouncer.run);
			window.removeEventListener('resize', this.loadNextIfNeededDebouncer.run);
		}
	}
	public init(): void {
		// If setup previously then undo setup and redo
		this.destroy();

		this.resultsEl = document.getElementById(this.options.frameId);
		this.paginationEl = document.getElementsByClassName(this.options.paginationClass)[0] as HTMLElement;

		// Nothing to setup
		if (!this.resultsEl || !this.paginationEl)
			return;

		this.paginationEl.classList.add('isAutoLoadPage--setup');
		let currentlyLoading = false;

		this.loadNextIfNeededDebouncer = new Debouncer((event: Event) => {
			const bottomOfResults = this.resultsEl.getBoundingClientRect().bottom + window.pageYOffset;
			const bottomOfScrollableArea = (this.scrollableAreaEl === window ? window.pageYOffset : (<HTMLElement>this.scrollableAreaEl).scrollTop) + window.innerHeight;

			if (bottomOfResults - bottomOfScrollableArea <= this.options.loadWhenDistanceFromBottom) {
				// Near bottom of results, load next page.
				this.loadNextPage();
			}
		});

		// Setup to check if next page needs to loaded when window scrolled or window resized
		this.scrollableAreaEl.addEventListener('scroll', this.loadNextIfNeededDebouncer.run);
		window.addEventListener('load', this.loadNextIfNeededDebouncer.run);
		window.addEventListener('resize', this.loadNextIfNeededDebouncer.run);
	}

	private loadNextPage(): void {
		// Test no next page to load.
		if (!this.paginationEl || this.currentlyLoading)
			return;

		const nextEl = this.paginationEl.getElementsByClassName(this.options.nextClass)[0] as HTMLAnchorElement;
		if (!nextEl)
			return;

		let loadHref = nextEl.href;
		loadHref = loadHref.replace(/&?_=[0-9]+/, '');
		loadHref = loadHref.replace(/\?&/, '?');

		if (!loadHref)
			return;

		this.currentlyLoading = true;
		this.paginationEl.classList.add('autoLoadPage--isLoading');

		// Load next page
		const htmlAjaxAction = (response: string) => {
			const newPage: Document = convertToDoc(response);

			// Append new results
			const newItems = newPage.getElementById(this.options.frameId).getElementsByClassName(this.options.resultsClass);
			const newItemFragment = document.createDocumentFragment();

			for (let i = newItems.length - 1; i >= 0; i--) {
				newItemFragment.appendChild(document.importNode(newItems[i], true));
			}

			this.resultsEl.appendChild(newItemFragment);

			// Update pagination
			const newPagination = document.importNode(newPage.getElementsByClassName(this.options.paginationClass)[0], true);
			if (newPagination) {
				const paginationParentEl = this.paginationEl.parentElement;
				paginationParentEl.removeChild(this.paginationEl);
				this.paginationEl = paginationParentEl.appendChild(newPagination) as HTMLElement;
				this.paginationEl.classList.add('isAutoLoadPage--setup');


				const nextEl = this.paginationEl.getElementsByClassName(this.options.nextClass)[0];
				if (!nextEl) {
					this.paginationEl.classList.add('autoLoadPage--noNext');
				}
			} else {
				this.paginationEl.parentElement.removeChild(this.paginationEl);
				this.paginationEl = undefined;
			}

			// Set back so another page can load.
			this.currentlyLoading = false;

			// Check if even though we just loaded a page we need to load another one to fill the area.
			this.loadNextIfNeededDebouncer.runNow();
		};

		const doHtmlAjax = (handleResponse: any, requestUrl: string = window.location.href): void => {
			function status(response: Response): Promise<any> {
				if (response.ok) {
					return Promise.resolve(response);
				} else {
					return Promise.reject(new Error(response.statusText));
				}
			}

			fetch(requestUrl)
				.then(status)
				.then(response => response.text())
				.then(handleResponse)
				.then(() => {
					this.init();
				})
				.catch(error => {
					console.warn(error);
				});
		};
		doHtmlAjax(htmlAjaxAction, nextEl.href);
	}
}